/**
 * Admin SSO via Auth0 (staff).
 *
 * Auth model (Checkpoint #4): staff sign in with Auth0 SSO; clients use passkeys
 * in the web app. The role lives in Aurora `users.primary_role`; Auth0
 * Organizations carry the staff invite + role assignment, and the Action emits
 * `https://re-platform/roles` on the ACCESS token (see auth0-action-token-targeting).
 *
 * The Auth0 *access token* (RS256, audience = the API) is used directly as the
 * API bearer — the backend `CompositeJWTValidator` accepts it and
 * `get_tenant_context` derives tenant + role from it. We use
 * cacheLocation:"localstorage" + useRefreshTokens so the bearer survives a page
 * reload without a redirect bounce.
 *
 * When the VITE_AUTH0_* env vars are absent (local dev), the app stays in "dev"
 * mode and the sidebar token-paste is the auth path instead — see lib/api.ts.
 */

import {
  Auth0Client,
  type Auth0ClientOptions,
  type User,
} from "@auth0/auth0-spa-js";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { loadAdminToken } from "./api";
import { saveActingTenant } from "./actingTenant";
import { claimsFromToken, rolesFromClaims, rolesFromToken } from "./roles";

/**
 * Dev mode has no Auth0 user object. Derive a light identity from the pasted
 * (or mock) session JWT's claims so the profile/header show who's signed in.
 */
function devUserFromToken(): User | null {
  const c = claimsFromToken(loadAdminToken());
  if (!c) return null;
  const name = (c.name ?? c.full_name) as string | undefined;
  const email = c.email as string | undefined;
  if (!name && !email) return null;
  return {
    name: name ?? email,
    email,
    picture: c.picture as string | undefined,
  };
}

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
const orgId = import.meta.env.VITE_AUTH0_ORG_ID;

export function isAuth0Configured(): boolean {
  return Boolean(domain && clientId && audience);
}

let _client: Auth0Client | null = null;
function client(): Auth0Client {
  if (_client) return _client;
  const options: Auth0ClientOptions = {
    domain: domain!,
    clientId: clientId!,
    authorizationParams: {
      audience: audience!,
      // Auth0 redirects back to the bare origin (Allowed Callback URL =
      // https://admin.<domainRoot> + http://localhost:5174); AuthProvider
      // handles the ?code&state on mount.
      redirect_uri: window.location.origin,
      scope: "openid profile email",
      // orgId comes from VITE_AUTH0_ORG_ID (a per-environment GitHub repo
      // variable, not hardcoded in source). When set, Auth0 login flow must be
      // "Pre-select Organization using organization parameter". When absent,
      // Auth0 falls back to its configured prompt (local dev token-paste path).
      ...(orgId && { organization: orgId }),
    },
    cacheLocation: "localstorage",
    useRefreshTokens: true,
  };
  _client = new Auth0Client(options);
  return _client;
}

/**
 * The API bearer: the Auth0 access token when SSO is configured, else the
 * pasted dev token. Returns null on any failure so the request goes out
 * anonymous (the gate already keeps unauthenticated users off the app).
 * The SDK can also return no token at all (an expired Auth0 session); that is
 * null too, exactly as the request layer already treats it.
 */
export async function getApiToken(
  loadDevToken: () => string | null,
): Promise<string | null> {
  if (!isAuth0Configured()) return loadDevToken();
  try {
    return (await client().getTokenSilently()) || null;
  } catch {
    return null;
  }
}

interface AuthState {
  ready: boolean;
  mode: "auth0" | "dev";
  isAuthenticated: boolean;
  user: User | null;
  /** The caller's `https://re-platform/roles` claim — drives UI role gating. */
  roles: string[];
  login: () => void;
  logout: () => void;
  /**
   * Re-authenticate into a specific Auth0 Organization (brokerage switcher for
   * dual-licensed staff). Redirects to Auth0 with the `organization` param so
   * the returned access token is scoped to that tenant. No-op in dev mode.
   */
  switchOrganization: (auth0OrgId: string) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode: "auth0" | "dev" = isAuth0Configured() ? "auth0" : "dev";
  const [ready, setReady] = useState(mode === "dev");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(() =>
    mode === "dev" ? devUserFromToken() : null,
  );
  // In dev mode the role comes from the pasted session JWT (re-read on reload,
  // which is what the sidebar Save triggers); in auth0 mode it's loaded from the
  // id-token claims below.
  const [roles, setRoles] = useState<string[]>(() =>
    mode === "dev" ? rolesFromToken(loadAdminToken()) : [],
  );

  useEffect(() => {
    if (mode === "dev") return;
    let cancelled = false;
    void (async () => {
      const c = client();
      const params = new URLSearchParams(window.location.search);
      if (params.has("code") && params.has("state")) {
        try {
          await c.handleRedirectCallback();
        } catch {
          // fall through — isAuthenticated() below stays false, gate shows login
        } finally {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        }
      }
      const authed = await c.isAuthenticated();
      if (cancelled) return;
      setIsAuthenticated(authed);
      if (authed) {
        setUser((await c.getUser()) ?? null);
        const claims = await c.getIdTokenClaims();
        if (!cancelled) {
          setRoles(rolesFromClaims(claims as Record<string, unknown>));
        }
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      mode,
      isAuthenticated,
      user,
      roles,
      login: () => void client().loginWithRedirect(),
      logout: () => {
        // A stale acting tenant must not leak into the next sign-in.
        saveActingTenant(null);
        void client().logout({
          logoutParams: { returnTo: window.location.origin },
        });
      },
      switchOrganization: (auth0OrgId: string) => {
        if (mode === "dev") return;
        void client().loginWithRedirect({
          authorizationParams: { organization: auth0OrgId },
        });
      },
    }),
    [ready, mode, isAuthenticated, user, roles],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
